import { useState } from "react";
import { fmtDec } from "utils/format";

const CONC_MAX_LABEL = {
  "Asset Class": "Max Class",
  "Region":      "Max Region",
  "Currency":    "Max Currency",
  "Industry":    "Max Industry",
  "Single Name": "Max Name",
};

export function concColour(ratio) {
  return ratio <= 0.95 ? "#22d47e" : ratio <= 1.05 ? "#C9A84C" : "#e05252";
}

export default function ConcChart({ rows }) {
  const [tooltip, setTooltip] = useState(null); // { d, x, y }

  const valid = (rows ?? []).filter((d) => d.max_weight != null);
  if (!valid.length) return null;

  const scaleMax =
    Math.max(...valid.flatMap((d) => [d.max_weight ?? 0, d.limit_value ?? 0])) * 1.1;

  return (
    <>
      <div className="trg-conc-rows">
        {valid.map((d) => {
          const ratio = d.limit_value ? d.max_weight / d.limit_value : null;
          const c     = ratio != null ? concColour(ratio) : "var(--dash-muted2)";
          const vp    = (d.max_weight / scaleMax * 100).toFixed(2);
          const lp    = d.limit_value != null ? (d.limit_value / scaleMax * 100).toFixed(2) : null;
          return (
            <div
              key={d.category}
              className="trg-conc-row"
              onMouseEnter={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setTooltip({ d, x: r.left + r.width / 2, y: r.top });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className="trg-conc-name">{d.category}</div>
              <div style={{ flex: 1, position: "relative", height: 16 }}>
                <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--dash-border)", transform: "translateY(-50%)" }} />
                <div style={{ position: "absolute", top: "50%", left: 0, width: `${vp}%`, height: 2, background: c, opacity: 0.3, transform: "translateY(-50%)" }} />
                <div style={{ position: "absolute", top: "50%", left: `${vp}%`, width: 8, height: 8, borderRadius: "50%", background: c, transform: "translate(-50%,-50%)", boxShadow: `0 0 5px ${c}88` }} />
                {lp != null && (
                  <div style={{ position: "absolute", top: 2, bottom: 2, left: `${lp}%`, width: 1.5, background: "var(--dash-border2)", transform: "translateX(-50%)" }} />
                )}
              </div>
              <div className="trg-conc-val">
                <span className="trg-conc-val-name">{d.category_name || "—"}</span>
                <span className="trg-conc-val-pct" style={{ color: c }}>{fmtDec(d.max_weight)}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {tooltip && (() => {
        const { d } = tooltip;
        const maxLabel = CONC_MAX_LABEL[d.category] || "Max";
        return (
          <div style={{
            position: "fixed", pointerEvents: "none", zIndex: 1200,
            left: tooltip.x, top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
            background: "rgba(8,14,24,0.96)",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 6, padding: "6px 10px",
            fontFamily: "var(--dash-font-m)", fontSize: "0.6rem",
            color: "rgba(255,255,255,0.85)", whiteSpace: "nowrap",
            boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
          }}>
            <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.5rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dash-muted)", marginBottom: 4 }}>
              {d.category}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {d.category_name && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span style={{ color: "var(--dash-muted)" }}>{maxLabel}</span>
                  <span style={{ color: "rgba(255,255,255,0.9)" }}>{d.category_name}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span style={{ color: "var(--dash-muted)" }}>Max Weight</span>
                <span style={{ color: "rgba(255,255,255,0.9)" }}>{fmtDec(d.max_weight)}%</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span style={{ color: "var(--dash-muted)" }}>Limit</span>
                <span style={{ color: "rgba(255,255,255,0.9)" }}>
                  {d.limit_value != null ? fmtDec(d.limit_value) + "%" : "—"}
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
